import { get, patch, post } from '../../../shared/utils/FetchV2.util';

const ENDPOINT = '/onboarding';
const ANALYSIS_ENDPOINT = '/onboarding/analysis';
const ROUTE_ENDPOINT = '/onboarding/route';

type OnboardingAnswer = {
	label: string;
	value: unknown;
	answered_at: string;
};

export type OnboardingAnswers = Record<string, OnboardingAnswer>;

// Typed read helper for OnboardingAnswers. Replaces the `(answers.x?.value as T
// | undefined)` cast scattered across step views and `isVisible` predicates.
// The caller still owns the type assertion — there's no schema — but the cast
// lives in one spot instead of being re-typed (and potentially mistyped) at
// every read site.
export const getAnswer = <T>(
	answers: OnboardingAnswers,
	key: string,
): T | undefined => answers[key]?.value as T | undefined;

// Authoritative source for what variant of onboarding a record belongs to.
// `heru_ai_service.onboarding.type` lives on the DB row and is set by the
// backend at creation time (`flow` for v2, `chat`/`form` for the legacy
// Samantha intake). The /onboarding endpoint normally only returns `flow`
// records, but echoing the field forward lets variant-detection logic stop
// guessing from answer keys (which breaks the moment the backend adds a
// new auto-stamped key).
export type OnboardingType = 'flow' | 'chat' | 'form';

export type OnboardingRecord = {
	id: number;
	type: OnboardingType | null;
	current_step: string | null;
	completed: boolean;
	answers: OnboardingAnswers;
	created_at: string;
	updated_at: string;
};

export type OnboardingPatch = Partial<
	Pick<OnboardingRecord, 'current_step' | 'completed'>
> & {
	answers?: Record<string, Omit<OnboardingAnswer, 'answered_at'>>;
	// Onboarding BUNDLE id (e.g. 'onboarding@3.1'). Write-once server-side: the
	// backend seeds it onto the record at creation and IGNORES it once
	// `answers.onboarding_flows_version` carries a stamp. The v3 host sends it on
	// EVERY patch — idempotent by write-once, so there is no "is this the first
	// write?" race. Absent on v2 patches (v2 stamps the bundle through answers).
	version?: string;
};

type ServerOnboarding = {
	id: number;
	type?: OnboardingType | null;
	current_step: string | null;
	completed: boolean | null;
	answers: OnboardingAnswers | null;
	created_at: string;
	updated_at: string;
};

// AnalysisResult mirrors the response of POST /onboarding/analysis. The
// backend (heru-user-management-engine) caches the analysis under
// answers.ai_analysis, so repeated calls within a session return the same
// payload with `cached: true` and the original generated_at timestamp.
export type AnalysisResult = {
	summary: string;
	obligations: string;
	recommendations: string;
	generated_at: string;
	cached: boolean;
	answers_snapshot?: OnboardingAnswers;
};

const toRecord = (server: ServerOnboarding): OnboardingRecord => ({
	id: server.id,
	type: server.type ?? null,
	current_step: server.current_step,
	completed: server.completed ?? false,
	answers: server.answers ?? {},
	created_at: server.created_at,
	updated_at: server.updated_at,
});

export const getOnboardingService = async (): Promise<OnboardingRecord | null> => {
	const response = await get<ServerOnboarding | '' | null>({
		service: 'userManagementEngine',
		endpoint: ENDPOINT,
	});
	if (!response || typeof response !== 'object') return null;
	return toRecord(response);
};

export const patchOnboardingService = async (
	body: OnboardingPatch,
): Promise<OnboardingRecord> => {
	const response = await patch<ServerOnboarding>({
		service: 'userManagementEngine',
		endpoint: ENDPOINT,
		body,
	});
	return toRecord(response);
};

// postOnboardingAnalysisService triggers the AI analysis. It is idempotent on
// the server: by default the first call hits Claude, persists the result
// under answers.ai_analysis, and returns it; subsequent calls return the
// cached payload instantly with `cached: true`.
//
// Pass `{ refresh: true }` to bypass the cache and force a fresh generation
// (e.g. when the user finishes a flow or hits the manual "regenerate"
// button). Refresh requests still go through the daily rate limit (default
// 3 fresh generations per user per day, configurable via
// ONBOARDING_ANALYSIS_RATE_LIMIT on the backend); cache hits never consume
// the cupo.
export const postOnboardingAnalysisService = async (
	options?: { refresh?: boolean },
): Promise<AnalysisResult> => {
	const endpoint = options?.refresh
		? `${ANALYSIS_ENDPOINT}?refresh=true`
		: ANALYSIS_ENDPOINT;
	return post<AnalysisResult>({
		service: 'userManagementEngine',
		endpoint,
		body: {},
	});
};

// RouteResult mirrors POST /onboarding/route. The backend classifies the user's
// free-text intent (the "Otra cosa" goal) into the onboarding flow that best
// matches it. `goal` is always one of the routable goals (annual_2026 |
// regularize | from_zero) — the backend coerces unknowns to from_zero — so the
// caller can navigate on it without re-validating.
export type RouteResult = {
	goal: string;
	confidence: number;
	reason: string;
};

// postOnboardingRouteService asks the backend to classify free text into the
// onboarding flow that best matches the user's intent. It does not persist
// anything; the caller saves the resolved goal via patchOnboardingService.
export const postOnboardingRouteService = async (
	text: string,
): Promise<RouteResult> =>
	post<RouteResult>({
		service: 'userManagementEngine',
		endpoint: ROUTE_ENDPOINT,
		body: { text },
	});

const COMUNICADO_ANALYZE_ENDPOINT = '/comunicado/analyze';

// ComunicadoVerdict drives the routing decision after analyzing a SAT notice:
// the backend coerces unknown model outputs into this allowlist, so the caller
// can branch on it without re-validating.
export type ComunicadoVerdict =
	| 'recordatorio'
	| 'periodos'
	| 'pago_pendiente'
	| 'asat'
	| 'ilegible'
	| 'posible_fraude';

// Taxonomía de comunicados del backend (docs/comunicado-sat-taxonomia.md en
// heru-user-management-engine). `(string & {})` deja pasar valores futuros sin
// romper, conservando el autocomplete de los conocidos.
export type ComunicadoType =
	| 'obligaciones_por_vencer'
	| 'carta_invitacion'
	| 'diferencias_pago'
	| 'requerimiento_omision'
	| 'opinion_de_cumplimiento'
	| 'no_es_comunicado_sat'
	| 'buzon_tributario_generico';

export type ComunicadoPeriod = {
	// "YYYY-MM" (mensual) o "YYYY" (anual)
	period: string;
	// ej. "ISR plataformas tecnológicas"
	obligation?: string;
};

// Allowlist de CTAs del backend (validActionKinds): el modelo solo puede
// proponer estos kinds; cualquier otro se trata como fila informativa.
export type ComunicadoActionKind =
	| 'verify_acuse'
	| 'pay_capture_line'
	| 'activate_plan'
	| 'buy_regularization'
	| 'buy_asat'
	| 'reupload'
	| 'check_buzon'
	| 'beware_fraud'
	| 'sat_clarification';

export type ComunicadoAction = {
	kind: ComunicadoActionKind | (string & {});
	label: string;
	detail?: string;
};

// ComunicadoAnalysisResult mirrors POST /comunicado/analyze: Heru-tone prose
// (summary/obligations/recommendations, light Markdown) plus the structured
// block (verdict/periods/actions) the regularization flow routes on. periods,
// deadline and folio only carry values that appear verbatim in the notice.
export type ComunicadoAnalysisResult = {
	summary: string;
	obligations: string;
	recommendations: string;
	// Los campos estructurados (verdict/periods/actions/…) llegaron en una
	// versión posterior del backend: un deploy anterior responde solo la prosa,
	// así que el caller debe tolerar su ausencia.
	comunicado_type?: ComunicadoType | (string & {});
	verdict?: ComunicadoVerdict;
	urgency?: 'alta' | 'media' | 'baja';
	periods: ComunicadoPeriod[];
	deadline?: string;
	folio?: string;
	actions: ComunicadoAction[];
	model: string;
	generated_at: string;
};

export type ComunicadoAnalyzeBody = {
	text?: string;
	image_base64?: string;
	image_mime?: string;
};

// postComunicadoAnalyzeService sends the SAT notice the user uploaded (pasted
// text and/or an image/PDF as base64 — a data: URL prefix is accepted) for AI
// analysis. Stateless and rate-limited server-side (10 analyses per user per
// day → 429 once the cap is hit).
export const postComunicadoAnalyzeService = async (
	body: ComunicadoAnalyzeBody,
): Promise<ComunicadoAnalysisResult> => {
	const raw = await post<ComunicadoAnalysisResult>({
		service: 'userManagementEngine',
		endpoint: COMUNICADO_ANALYZE_ENDPOINT,
		body,
	});
	return { ...raw, periods: raw.periods ?? [], actions: raw.actions ?? [] };
};
